import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Navbar } from './components/layout';
import { ComingSoon, ErrorBoundary, EncryptionTest } from './components/common';
import { isLoggedIn } from './utils/auth';
import SessionManager from './components/common/SessionManager';
import SubscriptionGuard from './components/common/SubscriptionGuard';
import { DEFAULT_PUBLIC_PROGRAM_CODE, ROUTES } from './config/routes';
import { validateEnvironment } from './config/environment';
import { initializeEncryption } from './config/encryption';
import Footer from './components/layout/Footer';
import { GoogleAnalyticsRouteTracker } from './components/common/GoogleAnalyticsRouteTracker';


// Import CSS files explicitly
import './components/layout/Navbar.css';
import './components/layout/Header.css';
import './components/home/Home.css';
import './styles/footer.css';

// Lazy load components for better performance
const HomePage = lazy(() => import('./components/home').then(module => ({ default: module.HomePage })));
const Home = lazy(() => import('./components/home').then(module => ({ default: module.HomePage })));

// Auth pages
const LoginPage = lazy(() => import('./pages/auth').then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth').then(module => ({ default: module.RegisterPage })));
const ChooseSubscriptionPlanPage = lazy(() => import('./pages/auth').then(module => ({ default: module.ChooseSubscriptionPlanPage })));

const StudentInternships = lazy(() => import('./pages').then(module => ({ default: module.StudentInternships })));
const PublicInternships = lazy(() => import('./pages/public/internships'));
const MentorshipsPortal = lazy(() => import('./components/mentorships/MentorshipsPortal'));
const TestInternshipsApi = lazy(() => import('./components/TestInternshipsApi'));
const StudentDashboard = lazy(() => import('./pages/student/dashboard'));
const ApplicationsListPage = lazy(() => import('./pages/student/ApplicationsListPage'));
const ConversationsPage = lazy(() => import('./pages/student/ConversationsPage'));
const OrgDashboard = lazy(() => import('./pages/organization/org-dashboard'));
const StudentProfilePage = lazy(() => import('./pages/student/ProfilePage'));
const OrgProfilePage = lazy(() => import('./pages/organization/OrgProfilePage'));
const CreateInternshipPage = lazy(() => import('./pages/organization/CreateInternshipPage'));
const InternshipViewPage = lazy(() => import('./pages/organization/dashboard/ViewInternshipPage'));
const EditInternshipPage = lazy(() => import('./pages/organization/dashboard/EditInternshipPage'));
const CreateJobPage = lazy(() => import('./pages/organization/jobs/CreateJobPage'));
const EditJobPage = lazy(() => import('./pages/organization/jobs/EditJobPage'));
const ViewJobPage = lazy(() => import('./pages/organization/jobs/ViewJobPage'));
const ApplicationReviewPage = lazy(() => import('./pages/organization/dashboard/ApplicationReviewPage'));
const JobApplicationReviewPage = lazy(() => import('./pages/organization/dashboard/JobApplicationReviewPage'));
const ProfessionalServicesEnquiryDetailPage = lazy(() => import('./pages/organization/ProfessionalServicesEnquiryDetailPage'));
const OrgMentorshipApplicationsListPage = lazy(() => import('./pages/organization/MentorshipApplicationsListPage'));
const OrgMentorshipApplicationDetailsPage = lazy(() => import('./pages/organization/MentorshipApplicationDetailsPage'));
const OrgSubscriptionPage = lazy(() => import('./pages/organization/OrgSubscriptionPage'));
const ViewApplicationPage = lazy(() => import('./pages/student/ViewApplicationPage'));
const StudentInternshipDetailsPage = lazy(() => import('./pages/student/StudentInternshipDetailsPage'));
const MentorshipApplicationsListPage = lazy(() => import('./pages/student/MentorshipApplicationsListPage'));
const ViewMentorshipApplicationPage = lazy(() => import('./pages/student/ViewMentorshipApplicationPage'));
const ViewJobApplicationPage = lazy(() => import('./pages/student/ViewJobApplicationPage'));
const MentorDashboard = lazy(() => import('./pages/mentor/MentorDashboard'));
const MentorshipApplicationDetailsPage = lazy(() => import('./pages/mentor/MentorshipApplicationDetailsPage'));
const MentorProfilePage = lazy(() => import('./pages/mentor/ProfilePage'));
const SponsorDashboard = lazy(() => import('./pages/sponsor/SponsorDashboard'));
const SponsorProfilePage = lazy(() => import('./pages/sponsor/SponsorProfilePage'));
const StudentDetailsPage = lazy(() => import('./pages/sponsor/StudentDetailsPage'));
const ScholarshipDetailsPage = lazy(() => import('./pages/sponsor/ScholarshipDetailsPage'));
const NotificationsPage = lazy(() => import('./components/notifications/NotificationsPage'));

// Public pages
const AboutUsPage = lazy(() => import('./pages/public/AboutUsPage'));
const OurPartnersPage = lazy(() => import('./pages/public/OurCorporatePartnersPage'));
const AccoladesPage = lazy(() => import('./pages/public/AccoladesPage'));
const ContactUsPage = lazy(() => import('./pages/public/ContactUsPage'));
const InboxPage = lazy(() => import('./pages/public/InboxPage'));
const ScholarshipsPage = lazy(() => import('./pages/public/ScholarshipsPage'));
const LearningHubPage = lazy(() => import('./pages/public/LearningHubPage'));
const SurrogateLoginPage = lazy(() => import('./pages/public/SurrogateLoginPage'));
const ProgramsListPage = lazy(() => import('./pages/public/ProgramsListPage'));
const JobsPage = lazy(() => import('./pages/public/JobsPage'));
const NewsMediaPage = lazy(() => import('./pages/public/NewsMediaPage'));
const NewsMediaDetailPage = lazy(() => import('./pages/public/NewsMediaDetailPage'));
const ProgramEnrollmentPage = lazy(() =>
  import('./pages/programs').then((m) => ({ default: m.ProgramEnrollmentPage }))
);
const MyProgramEnrollmentViewPage = lazy(() =>
  import('./pages/programs').then((m) => ({ default: m.MyProgramEnrollmentViewPage }))
);
const PaymentReturnPage = lazy(() =>
  import('./modules/payment').then((m) => ({ default: m.PaymentReturnPage }))
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Enhanced PrivateRoute with better typing and redirect handling
interface PrivateRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  redirectTo = ROUTES.PUBLIC.HOME
}) => {
  const isAuthenticated = isLoggedIn();

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// Validate environment on app startup
validateEnvironment();

const App: React.FC = () => {
  // Initialize encryption system on app startup
  useEffect(() => {
    const initEncryption = async () => {
      try {
        const initResult = await initializeEncryption();
        if (initResult.success) {
          console.log('✅ Encryption system initialized successfully');
          console.log('Encryption config:', {
            enabled: initResult.config.enabled,
            algorithm: initResult.config.algorithm,
            transformation: initResult.config.transformation
          });
        } else {
          console.warn('⚠️ Encryption system initialization failed:', initResult.message);
        }
      } catch (error) {
        console.error('❌ Failed to initialize encryption system:', error);
      }
    };

    initEncryption();
  }, []);

  // Pressing Back after logging out can restore the old page from the browser's back/forward cache,
  // still showing the signed-in screen. Reload it so a private page redirects to home instead.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !isLoggedIn()) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <GoogleAnalyticsRouteTracker />
        <div className="App">
          <SessionManager />
          <SubscriptionGuard />
          <Navbar />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.PUBLIC.HOME} element={<HomePage />} />
              <Route path={ROUTES.PUBLIC.HOME_ALT} element={<Home />} />
              <Route path={ROUTES.PUBLIC.INTERNSHIPS} element={<PublicInternships />} />
              <Route path={ROUTES.PUBLIC.JOBS} element={<JobsPage />} />
              <Route path={ROUTES.PUBLIC.MENTORSHIPS} element={<MentorshipsPortal />} />
              <Route path={ROUTES.PUBLIC.LOGIN} element={<LoginPage />} />
              <Route path={ROUTES.PUBLIC.REGISTER} element={<RegisterPage />} />
              <Route path={ROUTES.PUBLIC.CHOOSE_PLAN} element={<ChooseSubscriptionPlanPage />} />

              {/* Additional Public Pages */}
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/our-partners" element={<OurPartnersPage />} />
              <Route path="/accolades" element={<AccoladesPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path={ROUTES.PUBLIC.LEARNING_HUB} element={<LearningHubPage />} />
              <Route path={ROUTES.PUBLIC.SURROGATE_LOGIN} element={<SurrogateLoginPage />} />
              <Route
                path={ROUTES.PUBLIC.PAYMENT_RETURN}
                element={<PaymentReturnPage />}
              />
              <Route
                path={ROUTES.PUBLIC.PROGRAM_PAYMENT_RETURN}
                element={<PaymentReturnPage />}
              />
              <Route
                path={ROUTES.PUBLIC.PROGRAM_DETAIL}
                element={<ProgramEnrollmentPage />}
              />
              <Route
                path={ROUTES.PUBLIC.PROGRAM_IGNITEX_2026_LEGACY}
                element={
                  <Navigate
                    to={`/programs/${DEFAULT_PUBLIC_PROGRAM_CODE}`}
                    replace
                  />
                }
              />

              <Route path={ROUTES.PUBLIC.EVENTS} element={
                <ComingSoon title="Events" subtitle="The <b>Events</b> feature is being developed now. Please check after some time" />
              } />
              <Route path={ROUTES.PUBLIC.NEWS_MEDIA} element={<NewsMediaPage />} />
              <Route path={ROUTES.PUBLIC.NEWS_MEDIA_DETAIL} element={<NewsMediaDetailPage />} />
              <Route path={ROUTES.PUBLIC.SCHOLARSHIPS} element={<ScholarshipsPage />} />
              <Route path={ROUTES.PUBLIC.PROGRAMS} element={<ProgramsListPage />} />
              <Route path={ROUTES.PUBLIC.COMMUNITY} element={
                <ComingSoon title="Community" subtitle="The <b>Community</b> feature is being developed now. Please check after some time" />
              } />
              <Route path={ROUTES.PUBLIC.REMOTE_HUB} element={
                <ComingSoon title="Remote Hub" subtitle="The <b>Remote Hub</b> feature is being developed now. Please check after some time" />
              } />
              <Route path={ROUTES.STUDENT.PROFILE} element={
                <PrivateRoute>
                  <StudentProfilePage />
                </PrivateRoute>
              } />

              {/* Student Protected Routes */}
              <Route path={ROUTES.STUDENT.DASHBOARD} element={
                <PrivateRoute>
                  <StudentDashboard />
                </PrivateRoute>
              } />
              <Route
                path={ROUTES.STUDENT.PROGRAM_ENROLLMENT_VIEW}
                element={
                  <PrivateRoute>
                    <MyProgramEnrollmentViewPage />
                  </PrivateRoute>
                }
              />
              <Route path={ROUTES.STUDENT.INTERNSHIPS} element={
                <PrivateRoute>
                  <StudentInternships />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.APPLICATIONS} element={
                <PrivateRoute>
                  <ApplicationsListPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.APPLICATION_DETAILS} element={
                <PrivateRoute>
                  <ViewApplicationPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.JOB_APPLICATION_DETAILS} element={
                <PrivateRoute>
                  <ViewJobApplicationPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.MENTORSHIP_APPLICATIONS} element={
                <PrivateRoute>
                  <MentorshipApplicationsListPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.MENTORSHIP_APPLICATION_DETAILS} element={
                <PrivateRoute>
                  <ViewMentorshipApplicationPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.CONVERSATIONS} element={
                <PrivateRoute>
                  <ConversationsPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.INTERNSHIP_DETAILS} element={
                <PrivateRoute>
                  <StudentInternshipDetailsPage />
                </PrivateRoute>
              } />

              {/* Education Routes */}
              <Route path={ROUTES.STUDENT.EDUCATION} element={
                <PrivateRoute>
                  <StudentProfilePage defaultTab="education" />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.EDUCATION_ADD} element={
                <PrivateRoute>
                  <StudentProfilePage defaultTab="education" />
                </PrivateRoute>
              } />
              <Route path={ROUTES.STUDENT.EDUCATION_EDIT} element={
                <PrivateRoute>
                  <StudentProfilePage defaultTab="education" />
                </PrivateRoute>
              } />

              {/* Organization Protected Routes */}
              <Route path={ROUTES.ORGANIZATION.DASHBOARD} element={
                <PrivateRoute>
                  <OrgDashboard />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.INTERNSHIP_VIEW} element={
                <PrivateRoute>
                  <InternshipViewPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.APPLICATION_REVIEW} element={
                <PrivateRoute>
                  <ApplicationReviewPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.JOB_APPLICATION_REVIEW} element={
                <PrivateRoute>
                  <JobApplicationReviewPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.PROFILE} element={
                <PrivateRoute>
                  <OrgProfilePage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.CREATE_INTERNSHIP} element={
                <PrivateRoute>
                  <CreateInternshipPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.EDIT_INTERNSHIP} element={
                <PrivateRoute>
                  <EditInternshipPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.CREATE_JOB} element={
                <PrivateRoute>
                  <CreateJobPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.JOB_VIEW} element={
                <PrivateRoute>
                  <ViewJobPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.EDIT_JOB} element={
                <PrivateRoute>
                  <EditJobPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.MENTORSHIP_APPLICATIONS} element={
                <PrivateRoute>
                  <OrgMentorshipApplicationsListPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.MENTORSHIP_APPLICATION_DETAILS} element={
                <PrivateRoute>
                  <OrgMentorshipApplicationDetailsPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.PROFESSIONAL_SERVICES_ENQUIRY_DETAILS} element={
                <PrivateRoute>
                  <ProfessionalServicesEnquiryDetailPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.ORGANIZATION.SUBSCRIPTION} element={
                <PrivateRoute>
                  <OrgSubscriptionPage />
                </PrivateRoute>
              } />

              <Route path={ROUTES.MENTOR.DASHBOARD} element={
                <PrivateRoute>
                  <MentorDashboard />
                </PrivateRoute>
              } />
              <Route path={ROUTES.MENTOR.PROFILE} element={
                <PrivateRoute>
                  <MentorProfilePage />
                </PrivateRoute>
              } />

              {/* Notifications Route */}
              <Route path="/notifications" element={
                <PrivateRoute>
                  <NotificationsPage />
                </PrivateRoute>
              } />

              {/* Inbox Route */}
              <Route path={ROUTES.INBOX} element={
                <PrivateRoute>
                  <InboxPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.MENTOR.EDUCATION} element={
                <PrivateRoute>
                  <MentorProfilePage defaultTab="education" />
                </PrivateRoute>
              } />
              <Route path={ROUTES.MENTOR.PREFERENCE} element={
                <PrivateRoute>
                  <MentorProfilePage defaultTab="preference" />
                </PrivateRoute>
              } />
              <Route path={ROUTES.MENTOR.EXPERIENCE} element={
                <PrivateRoute>
                  <MentorProfilePage defaultTab="experience" />
                </PrivateRoute>
              } />
              <Route path={ROUTES.MENTOR.MENTORSHIP_APPLICATION_DETAILS} element={
                <PrivateRoute>
                  <MentorshipApplicationDetailsPage />
                </PrivateRoute>
              } />

              {/* Sponsor Protected Routes */}
              <Route path={ROUTES.SPONSOR.DASHBOARD} element={
                <PrivateRoute>
                  <SponsorDashboard />
                </PrivateRoute>
              } />
              <Route path={ROUTES.SPONSOR.PROFILE} element={
                <PrivateRoute>
                  <SponsorProfilePage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.SPONSOR.STUDENT_DETAILS} element={
                <PrivateRoute>
                  <StudentDetailsPage />
                </PrivateRoute>
              } />
              <Route path={ROUTES.SPONSOR.SCHOLARSHIP_DETAILS} element={
                <PrivateRoute>
                  <ScholarshipDetailsPage />
                </PrivateRoute>
              } />

              {/* Utility Routes */}
              <Route path={ROUTES.UTILITY.TEST_API} element={<TestInternshipsApi />} />
              <Route path="/encryption-test" element={<EncryptionTest />} />

              {/* Catch-all route for 404 */}
              <Route path="*" element={
                <ComingSoon
                  title="Page Not Found"
                  subtitle="The page you're looking for doesn't exist."
                />
              } />
            </Routes>
          </Suspense>
          <Footer />
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App; 